import { createRouter, createWebHistory } from 'vue-router';
import { cancelPageScroll, getCarouselScrollTop, scrollToPagePosition } from '../utils/brandScroll';

// Lazy loading components for better performance
const AppHome = () => import(/* webpackChunkName: "home" */ '../components/AppHome.vue');
const PrivacyPolicy = () => import(/* webpackChunkName: "privacy" */ '../components/PrivacyPolicy.vue');
const CareerPage = () => import(/* webpackChunkName: "career" */ '../views/CareerPage.vue');
const CareerApplication = () => import(/* webpackChunkName: "career-application" */ '../views/CareerApplication.vue');
const LeaderboardSnapshots = () => import(/* webpackChunkName: "leaderboard" */ '../views/LeaderboardSnapshots.vue');
const WalletPage = () => import(/* webpackChunkName: "wallet" */ '../views/WalletPage.vue');
const YarnLandLevelEditor = () => import(/* webpackChunkName: "yarn-land-editor" */ '../views/YarnLandLevelEditor.vue');

const DummyFlowLevelEditor = () => import(/* webpackChunkName: "dummy-flow" */ '../views/DummyFlowLevelEditor.vue');
const routes = [
  // Keep the Book Sort editor out of production builds.
  process.env.NODE_ENV === 'development' ? {
    path: '/book-sort',
    name: 'BookSortLevelEditor',
    component: () => import(/* webpackChunkName: "book-sort-editor" */ '../views/BookSortLevelEditor.vue'),
    meta: {
      title: 'Book Sort Level Designer - Mola',
      description: 'Private Book Sort level authoring, simulation, generation, and certification tools.',
      fullScreen: true,
    },
  } : {
    path: '/book-sort',
    redirect: '/',
  },
  {
    path: '/dummy-flow',
    name: 'DummyFlowLevelEditor',
    component: DummyFlowLevelEditor,
    meta: { title: 'DummyFlow Level Designer - Mola', fullScreen: true },
  },
  {
    path: '/',
    component: AppHome,
    name: 'AppHome',
    meta: {
      title: 'Mola Games — A little break for play',
      description: "A little break for play. Discover Mola's mobile games, playful worlds, and everyday adventures."
    }
  },
  {
    path: '/career',
    component: CareerPage,
    name: 'CareerPage',
    meta: {
      title: 'Join Mola — Career at Mola Games',
      description: 'Help us make everyday breaks a little brighter. Discover why Mola and introduce yourself to our team.'
    }
  },
  {
    path: '/career/apply',
    component: CareerApplication,
    name: 'CareerApplication',
    meta: {
      title: 'Apply to Mola — Mola Games',
      description: 'Share your CV and portfolio with Mola. Help us make mobile games that brighten everyday breaks.'
    }
  },
  {
    path: '/privacy-policy',
    component: PrivacyPolicy,
    name: 'PrivacyPolicy',
    meta: {
      title: 'Privacy Policy - Mola',
      description: 'Privacy policy for Mola mobile gaming platform'
    }
  },
  {
    path: '/leaderboard',
    component: LeaderboardSnapshots,
    name: 'LeaderboardSnapshots',
    meta: {
      title: 'Bakery Merge Bounty Leaderboard - Mola',
      description: 'Download the Bakery Merge Bounty leaderboard snapshots that are archived daily on Walrus.'
    }
  },
  {
    path: '/wallet',
    component: WalletPage,
    name: 'WalletPage',
    meta: {
      title: 'Wallet - Mola',
      description: 'Connect your wallet and view your balances.'
    }
  },
  {
    path: '/yarn-land',
    component: YarnLandLevelEditor,
    name: 'YarnLandLevelEditor',
    meta: {
      title: 'Yarn Land Level Editor - Mola',
      description: 'Private Yarn Land voxel level editor for local JSON authoring.',
      fullScreen: true
    }
  }
];

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior(to, from, savedPosition) {
    cancelPageScroll();
    if (savedPosition) return savedPosition;
    if (to.meta.fullScreen) return { top: 0 };
    let top = 0;
    if (to.hash) {
      let target;
      try { target = document.getElementById(decodeURIComponent(to.hash.slice(1))); } catch { /* Ignore malformed anchors. */ }
      if (target) {
        const headerHeight = document.querySelector('.site-header')?.getBoundingClientRect().height || 0;
        top = target.getBoundingClientRect().top + window.scrollY - headerHeight - 16;
        const carousel = to.path === '/' && to.hash === '#games'
          ? target.querySelector('.game-carousel')
          : null;
        if (carousel) {
          const bounds = carousel.getBoundingClientRect();
          top = getCarouselScrollTop({
            top: bounds.top + window.scrollY,
            height: bounds.height,
            cardHeight: carousel.querySelector('.carousel-window')?.getBoundingClientRect().height || bounds.height,
            headerHeight,
            viewportHeight: window.innerHeight
          });
        }
      }
    }
    // Direct links and browser history land immediately. User-selected section
    // links get a consistent, cancellable animation independent of browser speed.
    if (!from.name) return { top };
    if (to.path !== from.path) {
      if (!to.hash) return { top: 0 };
      window.scrollTo({ top: 0, behavior: 'instant' });
    }
    return scrollToPagePosition(top).then(() => false);
  }
});

// Update document title based on route meta
router.beforeEach((to, from, next) => {
  cancelPageScroll();
  // Keep links to the former homepage Career section working.
  if (to.path === '/' && to.hash === '#career') {
    next({ path: '/career', query: to.query, replace: true });
    return;
  }
  document.title = to.meta.title || 'Mola Games';
  const description = to.meta.description || 'A little break for play. Mobile games by Mola.';
  for (const selector of ['meta[name="description"]', 'meta[property="og:description"]', 'meta[name="twitter:description"]']) {
    document.querySelector(selector)?.setAttribute('content', description);
  }
  for (const selector of ['meta[property="og:title"]', 'meta[name="twitter:title"]']) {
    document.querySelector(selector)?.setAttribute('content', document.title);
  }
  next();
});

export default router;
